import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, TrendUp, TrendDown, ArrowSquareOut, DownloadSimple } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tradingViewStockUrl } from "../../lib/tradingview";

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const REASON_LABEL = {
  weekly_20ema_break: "Weekly 20-EMA break",
  outranked: "Out-ranked",
  no_data: "No data",
};
const REASON_COLOR = {
  weekly_20ema_break: "text-neg border-neg bg-neg-soft",
  outranked: "text-mod border-mod bg-mod-soft",
  no_data: "text-muted-fg border-subtle",
};

const CSV_HEADERS = [
  "status",        // ACTIVE / NEW / REPLACED
  "symbol",
  "company_name",
  "rank",
  "entry",
  "stop_loss",
  "target",
  "risk_reward",
  "tech_score",
  "pct_change_today",
  "rsi",
  "adx",
  "macd_hist",
  "volume_multiple",
  "ema200",
  "base_range_pct",
  "candle_close_pct",
  "max_drawdown_30d_pct",
  "pct_below_52w_high",
  "rs_vs_nifty_pct",
  "sector_index",
  "sector_rs_vs_nifty_pct",
  "deliv_pct",
  "picked_at",
  "replaced_at",
  "exit",
  "pnl_pct",
  "drop_reason",
];

function escapeCsvCell(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

async function downloadCsv(api, days) {
  // Comprehensive monthly snapshot: active picks (ACTIVE/NEW) + replaced
  // picks (REPLACED). Each row carries entry / stop_loss / target / R:R.
  const { data } = await api.get(`/admin/strongest/full-history?days=${days}`);
  const rows = data?.rows || [];
  const header = CSV_HEADERS.join(",");
  const body = rows
    .map((r) => CSV_HEADERS.map((h) => escapeCsvCell(r[h])).join(","))
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `strongest-snapshot-${days}d-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return rows.length;
}

export default function StrongestHistoryModal({ open, onClose }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/admin/strongest/history?days=${days}&limit=200`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("strongest history load", e);
        setData({ items: [], stats: {}, window_days: days });
      })
      .finally(() => setLoading(false));
  }, [open, days]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="strongest-history-modal"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-app border border-subtle rounded-sm w-full max-w-5xl max-h-[88vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-subtle">
              <div className="flex items-center gap-2">
                <Clock size={14} weight="bold" className="text-pos" />
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
                  Strongest · Replacement History
                </div>
                <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-mod text-mod bg-mod-soft rounded-sm">
                  Admin Only
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex border border-subtle rounded-sm p-0.5 bg-surface">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDays(d)}
                      data-testid={`history-window-${d}`}
                      className={
                        "px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition " +
                        (days === d
                          ? "bg-pos-soft text-pos"
                          : "text-muted-fg hover:text-primary-fg")
                      }
                    >
                      {d}d
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const n = await downloadCsv(api, days);
                      if (!n) {
                        // eslint-disable-next-line no-alert
                        alert("Snapshot is empty for the selected window.");
                      }
                    } catch (e) {
                      console.error("CSV export failed", e);
                      // eslint-disable-next-line no-alert
                      alert("CSV export failed. Please try again.");
                    }
                  }}
                  data-testid="strongest-history-export"
                  className="px-2.5 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-pos hover:border-pos/40 transition flex items-center gap-1.5"
                  title="Download monthly snapshot (active + replaced) as CSV"
                >
                  <DownloadSimple size={11} weight="bold" /> CSV
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="strongest-history-close"
                  className="p-1.5 rounded-sm border border-subtle text-muted-fg hover:text-primary-fg hover:border-pos/40"
                >
                  <X size={13} weight="bold" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {loading ? (
                <div className="text-center text-muted-fg py-12">Loading history…</div>
              ) : !data || (data.items?.length ?? 0) === 0 ? (
                <div className="p-10 border border-subtle rounded-sm bg-surface text-center" data-testid="history-empty">
                  <Clock size={24} className="mx-auto text-muted-fg mb-3" />
                  <div className="text-primary-fg font-medium mb-1">No replacements yet</div>
                  <div className="text-sm text-muted-fg max-w-md mx-auto">
                    Drops are recorded automatically when a stock is replaced
                    in the Top 10 (weekly 20-EMA break or out-ranked by a
                    stronger candidate). Check back after a few scan cycles.
                  </div>
                </div>
              ) : (
                <>
                  {/* Stats strip */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
                    <Stat label="Total" value={data.stats.total_drops} />
                    <Stat label="Wins" value={data.stats.wins} valueClass="text-pos" />
                    <Stat label="Losses" value={data.stats.losses} valueClass="text-neg" />
                    <Stat label="Win Rate" value={`${data.stats.win_rate}%`} valueClass={data.stats.win_rate >= 50 ? "text-pos" : "text-mod"} />
                    <Stat label="Avg P&L" value={`${data.stats.avg_pnl >= 0 ? "+" : ""}${data.stats.avg_pnl}%`} valueClass={data.stats.avg_pnl >= 0 ? "text-pos" : "text-neg"} />
                  </div>

                  {/* Best/Worst row */}
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <Stat label="Best Drop" value={`+${data.stats.best_pnl}%`} valueClass="text-pos" />
                    <Stat label="Worst Drop" value={`${data.stats.worst_pnl}%`} valueClass="text-neg" />
                  </div>

                  {/* Reason breakdown */}
                  {Object.keys(data.stats.by_reason || {}).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-4 text-[10px] font-mono uppercase tracking-widest">
                      <span className="text-muted-fg">By reason:</span>
                      {Object.entries(data.stats.by_reason).map(([k, v]) => (
                        <span
                          key={k}
                          className={"px-2 py-0.5 border rounded-sm " + (REASON_COLOR[k] || "border-subtle text-muted-fg")}
                        >
                          {REASON_LABEL[k] || k}: {v}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Drops table */}
                  <div className="border border-subtle rounded-sm overflow-x-auto" data-testid="history-table">
                    <table className="w-full text-xs">
                      <thead className="bg-surface">
                        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">
                          <th className="px-3 py-2 text-left">Symbol</th>
                          <th className="px-3 py-2 text-left">Picked</th>
                          <th className="px-3 py-2 text-left">Dropped</th>
                          <th className="px-3 py-2 text-right">Held</th>
                          <th className="px-3 py-2 text-right">Entry</th>
                          <th className="px-3 py-2 text-right">Exit</th>
                          <th className="px-3 py-2 text-right">P&L</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {data.items.map((it) => {
                          const sym = (it.symbol || "").replace(".NS", "");
                          const positive = (it.pnl_pct || 0) >= 0;
                          return (
                            <tr key={`${it.symbol}-${it.dropped_at}`} className="border-t border-subtle hover:bg-surface/40">
                              <td className="px-3 py-2 text-primary-fg font-bold">{sym}</td>
                              <td className="px-3 py-2 text-muted-fg">{(it.picked_at || "").slice(0, 10)}</td>
                              <td className="px-3 py-2 text-muted-fg">{(it.dropped_at || "").slice(0, 10)}</td>
                              <td className="px-3 py-2 text-right text-secondary-fg tabular-nums">{it.days_held}d</td>
                              <td className="px-3 py-2 text-right text-secondary-fg tabular-nums">₹{fmt(it.entry)}</td>
                              <td className="px-3 py-2 text-right text-secondary-fg tabular-nums">₹{fmt(it.exit)}</td>
                              <td className={"px-3 py-2 text-right tabular-nums font-bold " + (positive ? "text-pos" : "text-neg")}>
                                {positive ? <TrendUp size={9} weight="bold" className="inline mr-0.5" /> : <TrendDown size={9} weight="bold" className="inline mr-0.5" />}
                                {positive ? "+" : ""}{fmt(it.pnl_pct, 2)}%
                              </td>
                              <td className="px-3 py-2">
                                <span className={"px-1.5 py-0.5 text-[9px] uppercase tracking-widest border rounded-sm " + (REASON_COLOR[it.drop_reason] || "border-subtle text-muted-fg")}>
                                  {REASON_LABEL[it.drop_reason] || it.drop_reason}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <a
                                  href={tradingViewStockUrl(it.symbol)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-muted-fg hover:text-pos"
                                  title="Open on TradingView"
                                >
                                  <ArrowSquareOut size={11} weight="bold" />
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Stat({ label, value, valueClass = "text-primary-fg" }) {
  return (
    <div className="border border-subtle rounded-sm p-3 bg-surface">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">
        {label}
      </div>
      <div className={"font-mono text-lg tabular-nums " + valueClass}>{value}</div>
    </div>
  );
}
